import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UpsaleBenefits } from '../../../../components/UpsaleBenefits/UpsaleBenefits';
import { UpsaleButton } from '../../../../components/UpsaleButton/UpsaleButton';
import { UpsaleCta } from '../../../../components/UpsaleCta/UpsaleCta';
import { UpsaleFeatureList } from '../../../../components/UpsaleFeatureList/UpsaleFeatureList';
import { Hero } from '../../../../components/UpsaleHero/Hero';
import { UpsaleHowItWorks } from '../../../../components/UpsaleHowItWorks/UpsaleHowItWorks';
import { UpsaleInfoSection } from '../../../../components/UpsaleInfoSection/UpsaleInfoSection';
import { UpsaleReview } from '../../../../components/UpsaleReview/UpsaleReview';
import { useRegistrationSection } from '../../../../providers/RegistrationSectionProvider/useRegistrationData';
import { UpsaleProgressChart } from '../../../../components/UpsaleProgressChart/UpsaleProgressChart';
import { UpsaleTransformation } from '../../../../components/UpsaleTransformation/UpsaleTransformation';
import { UpsaleFirstOffer } from '../../Components/UpsaleFirstOffer/UpsaleFirstOffer';
import { UpsaleSecondOffer } from '../../Components/UpsaleSecondOffer/UpsaleSecondOffer';
import { fbAddToCart } from '../../../../utils/FacebookEvents';
import { ampPaywall } from '../../../../utils/AmplitudeEvents';

export const Upsale = () => {
  const { content } = useRegistrationSection();
  const blocks = useMemo(() => content?.blocks || [], [content?.blocks]);
  useEffect(()=>{
    fbAddToCart();
    ampPaywall(content?.slug)
  },[])
  // Получаем индексы всех ctaBlock
  const ctaIndexes = useMemo(
    () => blocks
      .map((block, i) => block.blockName === 'ctaBlock' ? i : null)
      .filter(i => i !== null),
    [blocks]
  );
  // Рефы на все UpsaleCta
  const ctaRefs = useRef([]);
  ctaRefs.current = ctaIndexes.map(
    (_, i) => ctaRefs.current[i] || React.createRef()
  );

  // Нужно хранить, виден ли хотя бы один из CTA
  const [ctaVisible, setCtaVisible] = useState(false);

  useEffect(() => {
    if (!ctaRefs.current.length) return;

    const handleIntersection = (entries) => {
      // Если хотя бы один блок в зоне видимости — скрываем кнопку
      const anyVisible = entries.some(entry => entry.isIntersecting);
      setCtaVisible(anyVisible);
    };

    const observer = new window.IntersectionObserver(handleIntersection, {
      threshold: 0.1,
    });

    ctaRefs.current.forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, [blocks]);

  // Скролл к последнему CTA
  const handleAnchorClick = () => {
    const lastRef = ctaRefs.current[ctaRefs.current.length - 1];
    if (lastRef?.current) {
      lastRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Рендер
  let ctaBlockIdx = 0;
  return (
    <>
      {blocks.map((block, idx) => {
        switch (block.blockName) {
          case 'heroBlock':
            return <Hero key={idx} content={block} />;
          case 'featureListBlock':
            return <UpsaleFeatureList key={idx} content={block} />;
          case 'ctaBlock': {
            const ref = ctaRefs.current[ctaBlockIdx];
            ctaBlockIdx++;
            return <UpsaleCta key={idx} content={block} ref={ref} />;
          }
          case 'InfoSection':
            return <UpsaleInfoSection key={idx} content={block} />;
          case 'review':
            return <UpsaleReview key={idx} content={block} />;
          case 'Benefits':
            return <UpsaleBenefits key={idx} content={block} />;
          case 'HowItWorks':
            return <UpsaleHowItWorks key={idx} content={block} />;
          case 'anchorButton':
            // hide проп, чтобы скрыть кнопку, если любой CTA виден
            return (
              <UpsaleButton
                key={idx}
                hide={ctaVisible}
                onClick={handleAnchorClick}
              >
                {block.text}
              </UpsaleButton>
            );
          case 'ProgressChart':
            return <UpsaleProgressChart key={idx} content={block}/>
          case 'Transformation':
            return <UpsaleTransformation key={idx} content={block}/>
          case 'firstOffer': 
            return <UpsaleFirstOffer key={idx} content={block}/>
          case 'secondOffer':
            return <UpsaleSecondOffer key={idx} content={block}/>
          default:
            return null;
        }
      })}
    </>
  );
};
